import type {
  ChainPlugin,
  DecodedTransaction,
  ValidationResult,
  ValidationError,
  Warning,
} from '@txviz/core';
import {
  isValidHex,
  validateEthereumAddress,
  validateUint256,
  validateNonNegativeBigInt,
  generateCommonWarnings,
} from '@txviz/core';
import type { EthereumPluginInput, EthereumPluginTransactionInput } from './types.js';
import { isEIP712Input, isTransactionInput } from './types.js';
import { serializeEthereumTransaction } from './serializer.js';
import { hashEthereumTransaction } from './hasher.js';
import { hashEIP712TypedData, decodeEIP712TypedData } from './eip712.js';
import { decodeEthereumTransaction } from './decoder.js';

/**
 * Ethereum chain plugin.
 *
 * Handles both regular transactions (legacy, EIP-2930, EIP-1559, EIP-4844)
 * and EIP-712 typed data signing.
 */
export const ethereumPlugin: ChainPlugin<EthereumPluginInput> = {
  chain: 'ethereum',

  validateInput(input: EthereumPluginInput): ValidationResult {
    if (isEIP712Input(input)) {
      return validateEIP712(input);
    }
    if (isTransactionInput(input)) {
      return validateTransaction(input);
    }
    return {
      valid: false,
      errors: [{ field: 'input', message: 'Unrecognized Ethereum input format' }],
      warnings: [],
    };
  },

  serialize(input: EthereumPluginInput): Uint8Array {
    if (isEIP712Input(input)) {
      // For EIP-712, the "serialized" form is the hash itself
      // since the signing payload is: keccak256("\x19\x01" || domainSep || hashStruct)
      return hashEIP712TypedData(input);
    }
    if (isTransactionInput(input)) {
      return serializeEthereumTransaction(input.transaction);
    }
    throw new Error('Unrecognized Ethereum input format');
  },

  hash(serialized: Uint8Array): Uint8Array {
    // For EIP-712: serialized IS already the final hash
    // We detect this by checking if it's exactly 32 bytes (hash output)
    // For regular transactions: serialized is the RLP-encoded tx, so hash it
    if (serialized.length === 32) {
      // EIP-712: the serialize step already produced the final hash
      return serialized;
    }
    return hashEthereumTransaction(serialized);
  },

  async decode(input: EthereumPluginInput): Promise<DecodedTransaction> {
    if (isEIP712Input(input)) {
      return decodeEIP712TypedData(input);
    }
    if (isTransactionInput(input)) {
      return decodeEthereumTransaction(input.transaction, input.abi);
    }
    throw new Error('Unrecognized Ethereum input format');
  },
};

function validateTransaction(input: EthereumPluginTransactionInput): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: Warning[] = [];
  const tx = input.transaction;

  // Validate address
  if (tx.to !== null) {
    const addrError = validateEthereumAddress(tx.to, 'to');
    if (addrError) errors.push(addrError);
  }

  // Validate hex data
  if (!isValidHex(tx.data)) {
    errors.push({ field: 'data', message: 'Invalid hex calldata' });
  }

  // Validate numeric fields
  const numericChecks: [bigint, string][] = [
    [tx.chainId, 'chainId'],
    [tx.nonce, 'nonce'],
    [tx.value, 'value'],
    [tx.gasLimit, 'gasLimit'],
  ];

  for (const [value, field] of numericChecks) {
    const err = validateNonNegativeBigInt(value, field);
    if (err) errors.push(err);
  }

  const uint256Check = validateUint256(tx.value, 'value');
  if (uint256Check) errors.push(uint256Check);

  // Chain-specific gas field validation
  if (tx.type === 'legacy' || tx.type === 'eip2930') {
    const gasPrice = 'gasPrice' in tx ? tx.gasPrice : undefined;
    if (gasPrice !== undefined) {
      const err = validateNonNegativeBigInt(gasPrice, 'gasPrice');
      if (err) errors.push(err);
    }
  }

  if (tx.type === 'eip1559' || tx.type === 'eip4844') {
    const mfpg = 'maxFeePerGas' in tx ? tx.maxFeePerGas : undefined;
    const mppfg = 'maxPriorityFeePerGas' in tx ? tx.maxPriorityFeePerGas : undefined;
    if (mfpg !== undefined) {
      const err = validateNonNegativeBigInt(mfpg, 'maxFeePerGas');
      if (err) errors.push(err);
    }
    if (mppfg !== undefined) {
      const err = validateNonNegativeBigInt(mppfg, 'maxPriorityFeePerGas');
      if (err) errors.push(err);
    }
    if (mfpg !== undefined && mppfg !== undefined && mppfg > mfpg) {
      warnings.push({
        message: 'maxPriorityFeePerGas exceeds maxFeePerGas — the excess will be wasted',
        severity: 'warning',
      });
    }
  }

  // Common warnings
  warnings.push(...generateCommonWarnings({ to: tx.to, value: tx.value, data: tx.data }));

  return { valid: errors.length === 0, errors, warnings };
}

function validateEIP712(input: { domain: unknown; types: unknown; primaryType: unknown; message: unknown }): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: Warning[] = [];

  if (typeof input.primaryType !== 'string' || input.primaryType.length === 0) {
    errors.push({ field: 'primaryType', message: 'primaryType must be a non-empty string' });
  }

  if (typeof input.types !== 'object' || input.types === null) {
    errors.push({ field: 'types', message: 'types must be an object' });
  }

  if (typeof input.message !== 'object' || input.message === null) {
    errors.push({ field: 'message', message: 'message must be an object' });
  }

  if (typeof input.domain !== 'object' || input.domain === null) {
    errors.push({ field: 'domain', message: 'domain must be an object' });
  }

  warnings.push({
    message: 'EIP-712 typed data signing — review all fields carefully before signing',
    severity: 'info',
  });

  return { valid: errors.length === 0, errors, warnings };
}
